import { useRef } from "react";

/**
 * Keep the last value a query returned on screen while a new one is in flight.
 *
 * Convex's `useQuery` is a *subscription*, not a fetch. Change its arguments —
 * a filter chip, a search box — and it tears down the old subscription and
 * returns `undefined` until the new one lands. Every screen in this app then
 * hits the same guard at the top of its render (`if (data === undefined)
 * return <Loading/>`) and unmounts *everything*: the heading, the chips the
 * thumb just tapped, the list. On a 390px phone that reads as the app breaking
 * on every tap, not as loading.
 *
 * Wrapping the query in this makes the argument change a *refinement* of a
 * screen that already has data rather than a fresh load of one that does not:
 *
 *   first render      `undefined` in, `undefined` out — the skeleton still
 *                     shows, because the screen genuinely has nothing yet.
 *   filter changed    the previous payload out, so the screen stays mounted
 *                     and `data-screen-state` stays "ready", which is true:
 *                     it has data, it is showing it, and it is about to show
 *                     a better one.
 *
 * Written during render rather than in an effect on purpose. An effect runs
 * *after* the paint that would have blanked the screen, which is the one frame
 * this exists to prevent. Assigning to a ref during render is safe here because
 * the ref is only ever a cache of a value React already gave us — re-running
 * the render with the same input writes the same thing.
 *
 * It cannot distinguish "still loading" from "loaded, then re-loading". That
 * is the whole point; a screen that needs to know shows its own inline
 * indicator rather than swapping the page for a skeleton.
 */
export function useStable<T>(value: T | undefined): T | undefined {
  const last = useRef(value);
  if (value !== undefined) last.current = value;
  return value ?? last.current;
}
